import { getCountryHubspotName } from "@/utils/constants/regions";
import { calculateFeeTotal, calculateProjectTotals, FEE_FIELD_MAP } from "@/utils/pricer/pricerHelpers";

const calculateServiceFees = (project) =>
  Object.keys(FEE_FIELD_MAP).reduce((sum, feeKey) => sum + calculateFeeTotal(project, feeKey), 0);

const calculateAmountAskPayg = (project) => {
  if (project.has_ask_licence === true) return 0;
  const totals = calculateProjectTotals(project);
  const discount = project.ask_licence_discount || 0;
  return totals.n * discount;
};

// For the moment the sales say that this amount is just 0 
const calculateAmountAskLicence = () => 0;

/**
 * Mapping configuration for HubSpot fields
 * Maps project fields to HubSpot deal properties
 */
export const HUBSPOT_FIELD_MAPPINGS = [
  {
    key: "dealName",
    label: "Deal Name",
    hubspotProperty: "dealname",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_name || "",
    getProjectValue: (project) => project.name || "",
    formatValue: (value) => value || "-",
  },
  {
    key: "dealOwner",
    label: "Deal Owner",
    hubspotProperty: "hubspot_owner_id",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_owner_sales || "",
    getProjectValue: null,
    formatValue: (value) => value || "-",
  },
  {
    key: "finalClient",
    label: "Final Client",
    hubspotProperty: "final_client",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_final_client || "",
    getProjectValue: (project) => project.end_client || "",
    formatValue: (value) => value || "-",
  },
  {
    key: "targets",
    label: "Targets",
    hubspotProperty: "cibles",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_cibles || "",
    getProjectValue: (project) =>
      project.targets
        .filter((target) => target.active)
        .map((target) => target.description)
        .join(", ") || "",
    formatValue: (value) => (value ? (value.length > 30 ? `${value.slice(0, 30)}...` : value) : "-"),
  },
  {
    key: "population",
    label: "Population",
    hubspotProperty: "population",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_population || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";

      const activeTargets = project.targets.filter((t) => t.active);
      if (!activeTargets.length) return "";

      const uniqueTypes = new Set(
        activeTargets.map((t) => t.target_type).filter((type) => type === "b2c" || type === "b2b"),
      );

      if (uniqueTypes.size === 0) return "";
      if (uniqueTypes.size === 2) return "B2C et B2B";
      return uniqueTypes.has("b2c") ? "B2C" : "B2B";
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "genre",
    label: "Genre",
    hubspotProperty: "genre",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_genre || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";

      const activeTargets = project.targets.filter((target) => target.active);
      if (!activeTargets.length) return "";

      const hasMen = activeTargets.some((target) => target.gender?.men === true);
      const hasWomen = activeTargets.some((target) => target.gender?.women === true);

      const genders = [];
      if (hasMen) genders.push("Homme");
      if (hasWomen) genders.push("Femme");

      return genders.join(";") || "";
    },
  },
  {
    key: "ageMin",
    label: "Age Min",
    hubspotProperty: "age_min",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_age_min || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";
      const ages = project.targets
        .filter((target) => target.active)
        .map((target) => target.age_min)
        .filter((age) => age != null);
      return ages.length > 0 ? Math.min(...ages).toString() : "";
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "ageMax",
    label: "Age Max",
    hubspotProperty: "age_max",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_age_max || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";
      const ages = project.targets
        .filter((target) => target.active)
        .map((target) => target.age_max)
        .filter((age) => age != null);
      return ages.length > 0 ? Math.max(...ages).toString() : "";
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "countries",
    label: "Countries",
    hubspotProperty: "geographical_areas_n",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_geographical_areas_n || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";

      // Collect all unique country codes from all active targets.countries
      const countryCodes = new Set();
      project.targets.forEach((target) => {
        if (!target.active) return;
        if (target.countries) {
          target.countries.forEach((country) => {
            if (country.country) {
              countryCodes.add(country.country);
            }
          });
        }
      });

      const countryLabels = Array.from(countryCodes)
        .map((code) => getCountryHubspotName(code))
        .filter((label) => label);

      return countryLabels.join(";") || "";
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "numberOfRespondents",
    label: "Number of Respondents",
    hubspotProperty: "number_of_respondents_new",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_number_of_respondents_new || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "0";

      let total = 0;
      project.targets.forEach((target) => {
        if (!target.active) return;
        if (target.countries) {
          target.countries.forEach((country) => {
            const n = parseFloat(country.n) || 0;
            total += n;
          });
        }
      });

      return total.toString();
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "quotas",
    label: "Quotas",
    hubspotProperty: "quotanew",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_quotas || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";

      const activeTargets = project.targets.filter((t) => t.active);
      if (!activeTargets.length) return "";

      const mapValue = (v) => (v === "soft" ? "Soft" : v === "hard" ? "Hard" : "Flex");
      const getValues = (type) => [...new Set(activeTargets.map((t) => t[`quota_${type}`]).map(mapValue))];

      // Check if any target has both men and women (gender quota only applies in this case)
      const hasGenderQuota = activeTargets.some(
        (target) => target.gender?.men === true && target.gender?.women === true,
      );

      const quotas = [];
      [
        { type: "age", label: "Age" },
        { type: "gender", label: "Gender", condition: hasGenderQuota },
        { type: "geo", label: "Geography" },
      ].forEach(({ type, label, condition }) => {
        if (condition === false) return; // Skip if condition is explicitly false
        getValues(type).forEach((val) => quotas.push(`${label} - ${val}`));
      });

      if (
        activeTargets.some(
          (target) => target.quota_other !== "--none--" && target.quota_other !== "" && target.quota_other != null,
        )
      ) {
        quotas.push(`Other - Hard`);
      }

      return quotas.join(";");
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "numberOfQuestions",
    label: "Number of Questions",
    hubspotProperty: "number_of_questions",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_number_of_questions || "",
    getProjectValue: (project) => {
      if (!project.targets?.length) return "";
      const loiNumbers = project.targets
        .filter((target) => target.active)
        .map((target) => target.loi)
        .filter((val) => val != null)
        .map((val) => {
          const str = String(val).replace(/q$/i, ""); // Remove 'q' at the end (case insensitive)
          return parseFloat(str);
        })
        .filter((num) => !isNaN(num));
      return loiNumbers.length > 0 ? Math.max(...loiNumbers) : "";
    },
    formatValue: (value) => (value ? `${value} questions` : "-"),
  },
  {
    key: "scripting",
    label: "Scripting",
    hubspotProperty: "scripting",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_scripting || "",
    getProjectValue: (project) => {
      const activeTarget = project.targets?.find((t) => t.active);
      if (!activeTarget) return "";
      return activeTarget.channel === "episto" ? "Episto platform" : "External tool";
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "amountConnect",
    label: "Amount Connect",
    hubspotProperty: "amount_connect",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_amount_connect || "",
    getProjectValue: (project) => {
      const total = calculateProjectTotals(project).total;
      return total - calculateServiceFees(project) - calculateAmountAskPayg(project) - calculateAmountAskLicence(project);
    },
    formatValue: (value) => value || "-",
  },
  {
    key: "cpi",
    label: "CPI",
    hubspotProperty: "prix_par_repondant",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_prix_par_repondant || "",
    getProjectValue: (project) => calculateProjectTotals(project).cpiAvg,
    formatValue: (value) => (value ? `${value} €` : "-"),
  },
  {
    key: "amountAskPayg",
    label: "Amount Ask PAYG",
    hubspotProperty: "amount_ask_payg",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_amount_ask_payg || "",
    getProjectValue: (project) => calculateAmountAskPayg(project).toString(),
    formatValue: (value) => value || "-",
  },
  {
    key: "amountAskLicence",
    label: "Amount Ask Licence",
    hubspotProperty: "amount_ask_licence",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_amount_ask_licence || "",
    getProjectValue: () => calculateAmountAskLicence().toString(),
    formatValue: (value) => value || "-",
  },
  {
    key: "askType",
    label: "Ask Type",
    hubspotProperty: "ask_type",
    // HubSpot option internal values are "true" (Licence) and "false" (PAYG)
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_ask_type || "",
    getProjectValue: (project) => (project.has_ask_licence === true ? "true" : "false"),
    formatValue: (value) => (value === "true" ? "Licence" : value === "false" ? "PAYG" : "-"),
  },
  {
    key: "marge_commerciale",
    label: "Marge commerciale",
    hubspotProperty: "marge_commerciale",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_marge_commerciale || "",
    getProjectValue: (project) => {
      const marginValues =
        project.targets
          ?.filter((target) => target.active)
          .map((target) => parseFloat(target.margin_manual) || parseFloat(target.margin_auto) || 0)
          .filter((margin) => margin > 0) || [];
      if (marginValues.length === 0) return 0;
      const average = marginValues.reduce((sum, val) => sum + val, 0) / marginValues.length;
      return average.toFixed(2);
    },
    formatValue: (value) => (value ? `${value}%` : "-"),
  },
  {
    key: "services_fees",
    label: "Services fees",
    hubspotProperty: "frais_de_service",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_frais_de_service || "",
    getProjectValue: (project) => calculateServiceFees(project),
    formatValue: (value) => (value ? `${value} €` : "-"),
  },
  {
    key: "translation",
    label: "Translation",
    hubspotProperty: "traduction",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_traduction || 0,
    getProjectValue: (project) => {
      return project.targets
        ?.filter((target) => target.active)
        .some((target) => target.countries?.some((country) => (parseFloat(country.translation_manual) || 0) > 0))
        ? "true"
        : "false";
    },
    formatValue: (value) => (value ? `${value}` : "-"),
  },
  {
    key: "translation_details",
    label: "Translation",
    hubspotProperty: "traduction___details",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_traduction_details || "",
    getProjectValue: (project) => {
      let total = 0;
      project.targets?.forEach((target) => {
        if (!target.active) return;
        target.countries?.forEach((country) => {
          total += parseFloat(country.translation_manual) || 0;
        });
      });
      return total;
    },
    formatValue: (value) => (value ? `${value} €` : "-"),
  },
  {
    key: "analyse",
    label: "Analyse",
    hubspotProperty: "analyse",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_analyse || "",
    getProjectValue: (project) => {
      return project.analysis_manual > 0 ? "true" : "false";
    },
    formatValue: (value) => (value ? `${value}` : "-"),
  },
  {
    key: "subject_of_the_study",
    label: "subject_of_the_study",
    hubspotProperty: "project",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_project || "",
    getProjectValue: (project) => project.survey_topic || "",
    formatValue: (value) => value || "-",
  },
  {
    key: "link",
    label: "Link",
    hubspotProperty: "details_cpi",
    getHubSpotValue: (hubSpotData) => hubSpotData.hs_details_cpi || "",
    getProjectValue: (project) =>
      (project.token ? `https://app.episto.fr/admin/pricer/projects/${project.token}/summary` : "-") || "",
    formatValue: (value) => (value ? `${value}` : "-"),
  },
  {
    key: "dealCompanies",
    label: "Companies",
    hubspotProperty: "deal_companies",
    getHubSpotValue: (hubSpotData) => {
      const company = hubSpotData.hs_deal_companies?.[0];
      return { id: company?.id || "", name: company?.name || "-" };
    },
    getProjectValue: (project) => ({ id: project.company_id || "", name: project.client || "-" }),
    formatValue: (value) => value?.name || "-",
  },
  {
    key: "dealContacts",
    label: "Contacts",
    hubspotProperty: "deal_contacts",
    getHubSpotValue: (hubSpotData) => {
      const contact = hubSpotData.hs_deal_contacts?.[0];
      const name = contact ? `${contact.firstname || ""} ${contact.lastname || ""}`.trim() : "-";
      return { id: contact?.id || "", name: name || "-" };
    },
    getProjectValue: (project) => ({ id: project.contact_id || "", name: project.contact_name || "-" }),
    formatValue: (value) => value?.name || "-",
  },
];

export const getFieldMappings = () => HUBSPOT_FIELD_MAPPINGS;

/**
 * Helper function to build properties object for HubSpot update
 * Only includes changed fields
 */
export const buildHubSpotProperties = (changedFields) => {
  const properties = {};

  changedFields.forEach((field) => {
    // Each field already has hubspotProperty and getNewValue wired in the modal
    if (!field.hubspotProperty || typeof field.getNewValue !== "function") return;

    const newValue = field.getNewValue();

    // For deal_companies and deal_contacts, extract only the id from the object
    if (field.hubspotProperty === "deal_companies" || field.hubspotProperty === "deal_contacts") {
      properties[field.hubspotProperty] = String(newValue?.id ?? "");
    } else {
      properties[field.hubspotProperty] = String(newValue ?? "");
    }
  });

  return properties;
};
